import React, { useMemo } from 'react';
import compareAsc from 'date-fns/compareAsc';
import parseISO from 'date-fns/parseISO';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import _isEqual from 'lodash/isEqual';
import lowerCase from 'lodash/lowerCase';
import isEmpty from 'validator/lib/isEmpty';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, Trans, useTranslation } from '@lyearn/i18n';
import {
  ErrorPosition,
  Fields,
  FieldType,
  RendererParams,
  useFormFieldValue,
  VisibilityOperators,
} from '@/components/Form';
import FormAddLocation from '@/components/Form/components/AddLocation';
import TimeZone from '@/components/Form/components/TimeZones';
import { renderAddContents } from '@/components/Form/renderers/renderAddContents';
import { renderTagsForEvent } from '@/components/Form/renderers/renderTags';
import { renderWorkspaceSettings } from '@/components/Form/renderers/renderWorkspaceSettings';
import AIFormEditor from '@/components/LyearnAI/components/Editor/components/AIFormEditor';
import AddCertificate from '@/modules/corporate/modules/certificates/components/AddCertificate';
import isInvalidCertificateSettings from '@/modules/corporate/modules/certificates/utils/validateCertificateSettings';
import { areRequiredTagsEmpty } from '@/modules/corporate/modules/tags/utils';
import { isWorkspaceSettingsEmpty } from '@/modules/corporate/modules/workspaceSettings/utils';
import AddSkills from '@/modules/skills/components/AddSkills';
import {
  CertificateSettingsInput,
  EventStatusEnum,
  EventTypeEnum,
  EventUnion,
  ExternalEventCreationStrategyEnum,
  ExternalEventProviderInfoInput,
  GeneratedByEnum,
  IntegrationProviderEnum,
  Maybe,
  ProviderType,
  RecurringTypeEnum,
  TextUseCaseEnum,
} from '@/types/schema';
import { getCurrentTimeZoneValue, getUtcToZonedTime } from '@/utils/timezone';

import { EventFormInput, FormSection } from '../../types';
import AddCover from '../AddCover';
import AddInstructor from '../AddInstructor';
import useInstructorValidation from '../AddInstructor/hooks/useInstructorValidation';
import ExternalEvent from '../ExternalEvent';
import { ExternalEventProviderType } from '../ExternalEvent/types';
import {
  getProvider,
  isExternalProviderEmpty,
  isExternalProviderUrlValid,
} from '../ExternalEvent/utils';
import RecordingSettings from '../RecordingSettings';
import RegistrationSettings from '../RegistrationSettings';
import SubType from '../SubType';
import EventAttendanceSettings from './components/EventAttendanceSettings';
import {
  ABSENT_THRESHOLD_DEFAULT_VALUE,
  COMPLETION_THRESHOLD_DEFAULT_VALUE,
} from './components/EventAttendanceSettings/consts';
import EventEditorVisibilitySettings from './components/EventEditorVisibilitySettings';
import { renderSessionDateRangePicker } from './components/FormDateRangePicker';
import RecurrenceSchedule from './components/RecurrenceSchedule';
import { validateRecurrenceSchedule } from './components/RecurrenceSchedule/utils';
import renderRecurringCategory from './components/RecurringCategory';
import EventCompletionSettings from './EventCompletionSettings';

const getProviderInput = (values: any) =>
  get(values, 'externalEventProps.externalEventProviderInfo');

const renderTimeZone = ({
  initialEvent,
  status,
}: {
  initialEvent?: EventUnion;
  status?: EventStatusEnum;
}) => {
  return ({
    value,
    errors,
    formik,
    setFieldValue,
  }: RendererParams<string | undefined | null, EventFormInput>) => {
    const { t } = useTranslation();
    const startTime = useFormFieldValue('startTime');
    const endTime = useFormFieldValue('endTime');
    const type = useFormFieldValue('type');

    const showError = Boolean(
      (formik.touched?.startTime || formik.touched?.endTime) && errors?.date,
    );

    const timezone = (value || getCurrentTimeZoneValue()) as string;

    const warnings = useMemo(() => {
      const warns = [];

      if (
        initialEvent &&
        !_isEqual(
          {
            startTime: new Date(initialEvent.startTime),
            endTime: new Date(initialEvent.endTime),
            timezone: initialEvent.timezone,
          },
          {
            startTime: new Date(startTime),
            endTime: new Date(endTime),
            timezone: value,
          },
        )
      ) {
        warns.push(t('Events:RecurringSession.RecurrenceScheduleEditWarning'));
      }

      if (
        !showError &&
        type === EventTypeEnum.Offline &&
        !isDateValid(parseISO(endTime), timezone)
      ) {
        warns.push(t('Events:Editor.BackdateSessionWarning'));
      }

      return warns;
    }, [showError, t, timezone, value, endTime, startTime, type]);

    return (
      <TimeZone
        disabled={status === EventStatusEnum.Completed}
        setFieldValue={setFieldValue}
        value={timezone}
        warnings={warnings}
      />
    );
  };
};

export const getFormFields = ({
  t,
  isEditMode,
  type,
  providers,
  shareable,
  contextualUserId,
  status,
  initialEvent,
  defaultCertificateSettings,
}: {
  t: TFunction;
  isEditMode: boolean;
  type: EventTypeEnum;
  providers: ExternalEventProviderType[];
  shareable?: boolean;
  contextualUserId?: string;
  status?: EventStatusEnum;
  initialEvent?: EventUnion;
  defaultCertificateSettings: CertificateSettingsInput;
}): Fields<EventFormInput> => {
  return {
    'externalEventProps.externalEventProviderInfo': {
      name: 'externalEventProps.externalEventProviderInfo',
      type: FieldType.SlowCustom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, onChange, touched, error }) => {
        const showError = Boolean(touched && error);

        return (
          <ExternalEvent
            disabled={status === EventStatusEnum.Completed}
            error={showError ? error : undefined}
            isEditMode={isEditMode}
            providers={providers}
            value={value}
            onChange={onChange}
          />
        );
      },
    },
    name: {
      name: 'name',
      placeholder: t('Events:Editor.TitlePlaceholder'),
      label: t('Common:Form.Title'),
      type: FieldType.Text,
      size: 'large',
      disabled: status === EventStatusEnum.Completed,
      props: {
        characterLimit: 100,
      },
    },
    'liveEventProps.subType': {
      name: 'liveEventProps.subType',
      label: t('Common:Filter.Type'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        const selectedSubType = value;

        return (
          <SubType
            disabled={status === EventStatusEnum.Completed}
            selectedSubType={selectedSubType}
            onChange={onChange}
          />
        );
      },
    },
    objective: {
      name: 'objective',
      placeholder: t('Events:Editor.ObjectivePlaceholder'),
      label: t('Common:Form.Objective'),
      type: FieldType.Multiline,
      size: 'large',
      disabled: status === EventStatusEnum.Completed,
      props: {
        characterLimit: 160,
        rows: 3,
        maxRows: 3,
      },
    },
    description: {
      name: 'description',
      label: t('Common:Form.Description'),
      required: false,
      type: FieldType.SlowCustom,
      Component: ({ value, setFieldValue, setNudgeState }) => {
        const fieldKey = 'description';
        const topic = useFormFieldValue('name');

        return (
          <AIFormEditor
            isTextAndMediaIndependent
            disabled={status === EventStatusEnum.Completed}
            nudgeKey="name"
            placeholder={t('Events:Editor.DescriptionPlaceholder')}
            setFieldValue={setFieldValue}
            setNudgeState={setNudgeState}
            text={value ?? ''}
            textFieldKey={fieldKey}
            textUseCase={TextUseCaseEnum.EventDescription}
            topic={topic}
          />
        );
      },
    },
    date: {
      name: 'date',
      type: FieldType.SlowCustom,
      Component: renderSessionDateRangePicker({ t, status, initialEvent }),
    },
    timezone: {
      name: 'timezone',
      type: FieldType.SlowCustom,
      Component: renderTimeZone({ status, initialEvent }),
    },
    recurringCategory: {
      name: 'recurringCategory',
      type: FieldType.SlowCustom,
      Component: renderRecurringCategory(isEditMode),
    },
    recurrenceSchedule: {
      name: 'recurrenceSchedule',
      type: FieldType.SlowCustom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, onChange, error, values }) => {
        return (
          <RecurrenceSchedule
            disabled={status === EventStatusEnum.Completed}
            errorConfig={error}
            initialEvent={initialEvent}
            value={value ?? undefined}
            values={values}
            onChange={onChange}
          />
        );
      },
      visibility: [
        {
          fieldKey: 'recurringCategory',
          value: RecurringTypeEnum.Recurring,
          operator: VisibilityOperators.Equal,
        },
      ],
    },
    cover: {
      name: 'cover',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange }) => {
        const defaultCover = useFormFieldValue('defaultCover');

        return (
          <AddCover
            cover={value!}
            defaultCover={defaultCover}
            disabled={status === EventStatusEnum.Completed}
            onChange={onChange}
          />
        );
      },
    },
    'offlineEventProps.location': {
      name: 'offlineEventProps.location',
      type: FieldType.Custom,
      Component: ({ value: location, onChange }) => {
        return (
          <FormAddLocation
            disabled={status === EventStatusEnum.Completed}
            location={location}
            onChange={onChange}
          />
        );
      },
    },
    instructorIds: {
      name: 'instructorIds',
      type: FieldType.Custom,
      errorPosition: ErrorPosition.Custom,
      Component: ({ value, onChange, error, touched }) => {
        const showError = Boolean(touched && error);

        return (
          <AddInstructor
            disabled={status === EventStatusEnum.Completed}
            error={showError ? error : undefined}
            instructorIds={value}
            onChange={onChange}
          />
        );
      },
    },
    skillIds: {
      name: 'skillIds',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return <AddSkills skillIds={value ?? []} onChange={onChange} />;
      },
    },
    tags: {
      name: 'tags',
      type: FieldType.Custom,
      Component: renderTagsForEvent,
    },
    certificateSettings: {
      name: 'certificateSettings',
      type: FieldType.SlowCustom,
      Component: ({ onChange, formik, errors }) => {
        const certificateSettings = useFormFieldValue('certificateSettings');
        const showError = Boolean(formik.touched?.certificateSettings && errors?.certificate);
        const warning =
          initialEvent &&
          initialEvent.recurringCategory === RecurringTypeEnum.Recurring &&
          !isEqual(
            initialEvent.certificateSettings || defaultCertificateSettings,
            certificateSettings,
          )
            ? t('Events:Editor.CertificateUpdateWarning')
            : undefined;

        return (
          <AddCertificate
            certificateSettings={certificateSettings}
            contentType="EVENT"
            error={showError}
            footer={
              [EventTypeEnum.Offline, EventTypeEnum.External].includes(type)
                ? t('Events:Editor.AddCertificateFooter')
                : undefined
            }
            showConditionSettings={type === EventTypeEnum.Live}
            warning={warning}
            onChange={onChange}
          />
        );
      },
    },
    prework: {
      name: 'prework',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        const completionSettings = useFormFieldValue('completionSettings');
        const { requirePreWorkPostWork } = completionSettings;
        return renderAddContents({
          disabled: status === EventStatusEnum.Completed,
          label: {
            title: t('Events:Editor.Prework'),
            description: t('Events:Editor.PreworkDescription'),
            actionText: t('Events:Editor.AddPrework'),
            deleteText: t('Events:Editor.RemovePrework'),
            modal: {
              title: t('Events:Editor.AddPrework'),
              description: t('Events:Editor.PreworkModalDescription'),
              primaryButtonText: t('Common:Actions.Add'),
            },
          },
          shareable,
          generatedBy: GeneratedByEnum.All,
          isPreWorkPostWorkRequired: requirePreWorkPostWork,
        })({ value, onChange });
      },
    },
    postwork: {
      name: 'postwork',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        const completionSettings = useFormFieldValue('completionSettings');
        const { requirePreWorkPostWork } = completionSettings;
        return renderAddContents({
          disabled: status === EventStatusEnum.Completed,
          label: {
            title: t('Events:Editor.Postwork'),
            description: t('Events:Editor.PostworkDescription'),
            actionText: t('Events:Editor.AddPostwork'),
            deleteText: t('Events:Editor.RemovePostwork'),
            modal: {
              title: t('Events:Editor.AddPostwork'),
              description: t('Events:Editor.PostworkModalDescription'),
              primaryButtonText: t('Common:Actions.Add'),
            },
          },
          shareable,
          generatedBy: GeneratedByEnum.All,
          isPreWorkPostWorkRequired: requirePreWorkPostWork,
        })({ value, onChange });
      },
    },
    visibilitySettings: {
      name: 'visibilitySettings',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => (
        <EventEditorVisibilitySettings
          contextualUserId={contextualUserId!}
          value={value!}
          onChange={onChange}
        />
      ),
    },
    subWorkspaceIds: {
      name: 'subWorkspaceIds',
      type: FieldType.SlowCustom,
      Component: renderWorkspaceSettings(t('Events:Session'), 'event'),
    },
    registrationSettings: {
      name: 'registrationSettings',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange, setFieldValue }) => {
        const recurringCategory = useFormFieldValue('recurringCategory');

        const externalProviderInfo = useFormFieldValue(
          'externalEventProps.externalEventProviderInfo',
        ) as ExternalEventProviderInfoInput | undefined;
        const selectedProviderData =
          externalProviderInfo && getProvider(externalProviderInfo.providerId, providers);
        const allowEnableWaitingRoom =
          selectedProviderData?.integrationProvider?.provider === IntegrationProviderEnum.Zoom &&
          externalProviderInfo?.creationStrategy === ExternalEventCreationStrategyEnum.Integration;

        const enableWaitingRoom = useFormFieldValue('externalEventProps.enableWaitingRoom') as
          | Maybe<boolean>
          | undefined;
        const onChangeEnableWaitingRoom = (val: boolean) =>
          setFieldValue('externalEventProps.enableWaitingRoom', val);

        return (
          <RegistrationSettings
            allowEnableWaitingRoom={allowEnableWaitingRoom}
            allowMultiSlotRegistration={recurringCategory === RecurringTypeEnum.Recurring}
            disabled={status === EventStatusEnum.Completed}
            enableWaitingRoom={enableWaitingRoom}
            registrationSettings={value ?? undefined}
            onChange={onChange}
            onChangeEnableWaitingRoom={onChangeEnableWaitingRoom}
          />
        );
      },
    },
    attendanceSettings: {
      name: 'attendanceSettings',
      type: FieldType.SlowCustom,
      Component: ({ setFieldValue, error }) => (
        <EventAttendanceSettings
          disabled={status === EventStatusEnum.Completed}
          error={error}
          setFieldValue={setFieldValue}
        />
      ),
    },
    recordingSettings: {
      name: 'recordingSettings',
      type: FieldType.SlowCustom,
      Component: ({ setFieldValue }) => {
        const externalEventRecordingSettings = useFormFieldValue(
          'externalEventProps.recordingSettings',
        );
        const liveEventRecordingSettings = useFormFieldValue('liveEventProps.recordingSettings');
        const liveEventSubType = useFormFieldValue('liveEventProps.subType');

        if (type === EventTypeEnum.External) {
          return (
            <RecordingSettings
              fieldKey="externalEventProps.recordingSettings"
              recordingSettings={externalEventRecordingSettings}
              setFieldValue={setFieldValue}
              status={status}
            />
          );
        }

        return (
          <RecordingSettings
            fieldKey="liveEventProps.recordingSettings"
            recordingSettings={liveEventRecordingSettings}
            setFieldValue={setFieldValue}
            status={status}
            subType={liveEventSubType}
          />
        );
      },
    },
    completionSettings: {
      name: 'completionSettings',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange, setFieldValue }) => {
        const liveEventProps = useFormFieldValue('liveEventProps');
        const recurringCategory = useFormFieldValue('recurringCategory');

        return (
          <EventCompletionSettings
            liveEventProps={liveEventProps}
            recurringCategory={recurringCategory}
            setFieldValue={setFieldValue}
            status={status}
            type={type}
            value={value!}
            onChange={onChange}
          />
        );
      },
    },
  };
};

export const getSectionLayout = ({
  t,
  type,
  subWorkspacesEnabled,
  isCertificateEnabled,
}: {
  t: TFunction;
  type: EventTypeEnum;
  subWorkspacesEnabled: boolean;
  isCertificateEnabled: boolean;
}) =>
  getEnabledItems([
    {
      id: FormSection.Basic,
      layout: [
        'externalEventProps.externalEventProviderInfo',
        'name',
        'objective',
        'description',
        'date',
        'timezone',
        'recurringCategory',
        'recurrenceSchedule',
      ],
      enabled: type === EventTypeEnum.External,
    },
    {
      id: FormSection.Basic,
      layout: [
        'name',
        'objective',
        'description',
        'date',
        'timezone',
        'recurringCategory',
        'recurrenceSchedule',
      ],
      enabled: type === EventTypeEnum.Offline,
    },
    {
      id: FormSection.Basic,
      layout: [
        'name',
        'liveEventProps.subType',
        'objective',
        'description',
        'date',
        'timezone',
        'recurringCategory',
        'recurrenceSchedule',
      ],
      enabled: type === EventTypeEnum.Live,
    },
    {
      id: FormSection.CoverImage,
      layout: ['cover'],
      enabled: true,
    },
    {
      id: FormSection.NativeSettings,
      subSections: getEnabledItems([
        {
          id: FormSection.Location,
          layout: ['offlineEventProps.location'],
          enabled: type === EventTypeEnum.Offline,
        },
        {
          id: FormSection.Instructor,
          name: t('Events:Editor.Instructors'),
          layout: ['instructorIds'],
          enabled: true,
        },
      ]),
      enabled: true,
    },
    {
      id: FormSection.RegistrationSettings,
      name: t('Events:Editor.RegistrationSettings'),
      layout: ['registrationSettings'],
      enabled: true,
    },
    {
      id: FormSection.AttendanceSettings,
      layout: ['attendanceSettings'],
      enabled: true,
    },
    {
      id: FormSection.SessionRecording,
      name: t('Events:Editor.ClassRecording'),
      layout: ['recordingSettings'],
      enabled: type === EventTypeEnum.Live || type === EventTypeEnum.External,
      visibility:
        type === EventTypeEnum.External
          ? [
              {
                fieldKey: 'externalEventProps.externalEventProviderInfo.creationStrategy',
                value: ExternalEventCreationStrategyEnum.Integration,
                operator: VisibilityOperators.Equal,
              },
            ]
          : undefined,
    },
    {
      id: FormSection.CompletionSettings,
      name: t('Events:Editor.CompletionSettings.CompletionSettings'),
      layout: ['completionSettings'],
      enabled: true,
    },
    {
      id: FormSection.Manage,
      subSections: getEnabledItems([
        {
          id: FormSection.Prework,
          layout: ['prework'],
          enabled: true,
        },
        {
          id: FormSection.Postwork,
          layout: ['postwork'],
          enabled: true,
        },
        {
          id: FormSection.VisibilitySettings,
          layout: ['visibilitySettings'],
          name: t('VisibilitySetting:VisibilitySettings'),
          description: t('VisibilitySetting:VisibilitySettingsDescription', {
            contentName: lowerCase(t('Events:Session')),
          }),
          enabled: true,
        },
        {
          id: FormSection.WorkspaceSettings,
          layout: ['subWorkspaceIds'],
          enabled: subWorkspacesEnabled,
        },
        {
          id: FormSection.Skills,
          name: t('Common:RoleDetails.Skills'),
          description: t('Skills:AddSkills.AddSkillsToBeAssessed'),
          layout: ['skillIds'],
          enabled: true,
        },
        {
          id: FormSection.Certificate,
          layout: ['certificateSettings'],
          enabled: isCertificateEnabled,
        },
        {
          id: FormSection.Tags,
          name: t('Common:Form.Tags'),
          description: t('Common:Form.TagsDescription'),
          layout: ['tags'],
          enabled: true,
        },
      ]),
      enabled: true,
    },
  ]);

export const isDateValid = (date: Date, timezone: string) => {
  return compareAsc(new Date(getUtcToZonedTime(timezone, new Date().toISOString())), date) < 0;
};

const isStartAndEndDateValid = (startTime: string, endTime: string) =>
  compareAsc(parseISO(startTime), parseISO(endTime)) < 0;

const isInValidBackdateSession = (
  startTime: string,
  endTime: string,
  timezone: string,
  type?: EventTypeEnum,
) =>
  type === EventTypeEnum.Offline &&
  !isDateValid(parseISO(startTime), timezone) &&
  isDateValid(parseISO(endTime), timezone);

export const validateSessionDate = (values: Partial<EventFormInput>) => {
  if (values.recurringCategory === RecurringTypeEnum.Recurring) {
    if (!isStartAndEndDateValid(values.startTime!, values.endTime!)) {
      return <Trans i18nKey="Events:Editor.DateAndTimeError" />;
    }

    return undefined;
  }

  if (
    values.type !== EventTypeEnum.Offline &&
    !isDateValid(parseISO(values.startTime!), values.timezone!)
  ) {
    return <Trans i18nKey="Events:Editor.StartDateError" />;
  }

  if (!isStartAndEndDateValid(values.startTime!, values.endTime!)) {
    return <Trans i18nKey="Events:Editor.DateAndTimeError" />;
  }

  if (isInValidBackdateSession(values.startTime!, values.endTime!, values.timezone!, values.type)) {
    return <Trans i18nKey="Events:Editor.BackdateSessionError" />;
  }

  return undefined;
};

export const validateForm =
  ({
    subWorkspacesEnabled,
    providers,
    validateSessionInstructors,
    status,
  }: {
    subWorkspacesEnabled: boolean;
    providers: ProviderType[];
    validateSessionInstructors: ReturnType<
      typeof useInstructorValidation
    >['validateSessionInstructors'];
    status?: EventStatusEnum;
  }) =>
  async (values: EventFormInput) => {
    const errors: any = {};
    if (status === EventStatusEnum.Completed) {
      // If event is completed, we need to validate only tags, certificate as other fields are disabled.
      if (areRequiredTagsEmpty(values.tags)) {
        errors.tags = <Trans i18nKey="Tags:Form.Error.TagsValuesEmpty" />;
      }

      if (isInvalidCertificateSettings(values.certificateSettings)) {
        errors.certificate = true;
      }

      return errors;
    }

    if (
      values.type === EventTypeEnum.External &&
      isExternalProviderEmpty(values.externalEventProps?.externalEventProviderInfo)
    ) {
      errors['externalEventProps.externalEventProviderInfo'] = (
        <Trans i18nKey="Events:Editor.ProviderEmptyError" />
      );
    } else if (
      values.type === EventTypeEnum.External &&
      values.externalEventProps?.externalEventProviderInfo.creationStrategy ===
        ExternalEventCreationStrategyEnum.Form &&
      isExternalProviderUrlValid(values.externalEventProps?.externalEventProviderInfo)
    ) {
      errors['externalEventProps.externalEventProviderInfo'] = (
        <Trans i18nKey="Events:Editor.InvalidLink" />
      );
    }

    if (!values.name || isEmpty(values.name)) {
      errors.name = <Trans i18nKey="Events:Editor.TitleEmptyError" />;
    }

    if (!values.objective || isEmpty(values.objective)) {
      errors.objective = <Trans i18nKey="Events:Editor.ObjectiveEmptyError" />;
    }

    if (areRequiredTagsEmpty(values.tags)) {
      errors.tags = <Trans i18nKey="Tags:Form.Error.TagsValuesEmpty" />;
    }

    if (isWorkspaceSettingsEmpty(subWorkspacesEnabled, values.subWorkspaceIds)) {
      errors.subWorkspaceIds = <Trans i18nKey="Workspace:WorkspaceSettingsEmpty" />;
    }

    if (values.recurrenceSchedule) {
      const recurrenceScheduleErrorConfig = validateRecurrenceSchedule({
        scheduleInfo: values.recurrenceSchedule,
        startTimeISO: values.startTime ? values.startTime : undefined,
      });

      if (recurrenceScheduleErrorConfig) errors.recurrenceSchedule = recurrenceScheduleErrorConfig;
    }

    const dateError = validateSessionDate(values);
    if (dateError) {
      errors.date = dateError;
    }

    const instructorValidationError = await validateSessionInstructors({
      providers,
      providerInput: getProviderInput(values),
      instructorIds: values.instructorIds,
    });

    if (instructorValidationError) {
      errors.instructorIds = instructorValidationError;
    }

    if (isInvalidCertificateSettings(values.certificateSettings)) {
      errors.certificate = true;
    }

    const {
      attendanceRecordingCompletionThreshold = COMPLETION_THRESHOLD_DEFAULT_VALUE,
      attendanceRecordingAbsentThreshold = ABSENT_THRESHOLD_DEFAULT_VALUE,
    } = values.completionSettings!;

    if (attendanceRecordingCompletionThreshold! <= attendanceRecordingAbsentThreshold!) {
      errors.attendanceSettings = <Trans i18nKey="Events:Editor.AttendanceSettings.Error" />;
    }

    return errors;
  };
